import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Moment } from "moment";

import EntityChart from "../entityChart";
import ReportListView from "../reportListView";
import Contact from "src/components/contact";
import EntityInsightPanel from "../entityInsightPanel";
import LeadListItem from "../leadsListItem";
import DealListItem from "../dealListItem";
import LeadsContentCard from "../contentCard";
import LeadsContentListItem from "../contentListCard";
import ClosedDealsCard from "../topDealsCard";
import ClosedDealsListItem from "../topDealsListItem";
import {
  fetchDealsDetailReport,
  fetchLeadsDetailReport,
  fetchPerformanceClosedDealValueGraphReport,
  fetchPerformanceClosedDealsGraphReport,
  fetchPerformanceDealValueGraphReport,
  fetchPerformanceDealsGraphReport,
  fetchPerformanceInteractionsGraphReport,
  fetchPerformanceLeadsGraphReport,
  fetchPerformanceTopContentClosedDeals,
  fetchPerformanceTopContentGeneratingLeads,
} from "src/redux/actions/reports";
import {
  CLOSED_DEALS_SCHEMA,
  CONTENT_LEADS_SCHEMA,
  DEALS_DETAILS_SCHEMA,
  LEADS_DETAILS_SCHEMA,
} from "src/constants/reports";

import { PerformanceTables, ReportTabs } from "src/types/reports";
import { useReportsAdoption } from "src/redux/actions/adoption/reports";

import _ from "lodash";
import styles from "./performance.module.scss";

interface PerformanceTabProps {
  reportDate?: ReportDate;
  filters?: any[];
  interval?: string;
}

interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}

const DefaultAssetIcon = "/images/defaultIcons/default_asset.jpg";

const PerformanceTab: React.FC<PerformanceTabProps> = ({
  reportDate,
  filters,
  interval
}) => {
  const dispatch = useDispatch();

  const { trackReportsToggledView } = useReportsAdoption();

  const { performance } = useSelector((state: any) => ({
    performance: _.get(state, "reports.performance", {}),
  }));

  const [contactInfo, setContactInfo] = useState({
    show: false,
    contactId: "",
  });

  const validFilters = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return [];

    return filters.filter((f) => {
      const { property, operator, value } = f || {};
      return (
        property?.type &&
        operator &&
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((v) => typeof v === "string" && v.trim() !== "")
      );
    });
  }, [filters]);

  const {
    interactions,
    leads,
    deals,
    dealValue,
    closedDeals,
    closedDealValue,
    topLeadsContent,
    contentClosedDeals,
    leadsDetail,
    dealsDetail,
  } = performance;

  const filteredDealValue = dealValue?.item?.filter(d => d.value >= 0);

  const baseFilters = useMemo(() => {
    if (!reportDate || !reportDate.startDate || !reportDate.endDate)
      return null;

    const formattedStartDate = reportDate.startDate.format("YYYY-MM-DD");
    const formattedEndDate = reportDate.endDate.format("YYYY-MM-DD");

    return {
      startDate: `${formattedStartDate}T00:00:00+00:00`,
      endDate: `${formattedEndDate}T23:59:59+00:00`,
      filters: filters || [],
      sortBy: "views",
      userType: "all",
      interval: interval || "day",
    };
  }, [reportDate, validFilters, interval]);

  const handleLoadMore = (assetType) => {
    switch (assetType) {
      case "topLeadsContent":
        if (topLeadsContent.pagination.currentPage < topLeadsContent.pagination.totalPages) {
          dispatch(
            fetchPerformanceTopContentGeneratingLeads({
              page: topLeadsContent.pagination.currentPage + 1,
              filter: baseFilters,
              limit: 20,
            })
          );
        }
        break;
  
      case "topClosedDealsContent":
        if (contentClosedDeals.pagination.currentPage < contentClosedDeals.pagination.totalPages) {
          dispatch(
            fetchPerformanceTopContentClosedDeals({
              page: contentClosedDeals.pagination.currentPage + 1,
              filter: baseFilters,
              limit: 20,
            })
          );
        }
        break;
  
      case "leadsDetail":
        if ((leadsDetail.pagination.currentPage + 1) * leadsDetail.pagination.recordsPerPage < leadsDetail.pagination.totalRecordsCount) {
          dispatch(
            fetchLeadsDetailReport({
              page: leadsDetail.pagination.currentPage + 1,
              filter: baseFilters,
              limit: 20,
            })
          );
        }
        break;

      case "dealsDetail":
        if ((dealsDetail.pagination.currentPage + 1) * dealsDetail.pagination.recordsPerPage < dealsDetail.pagination.totalRecordsCount) {
          dispatch(
            fetchDealsDetailReport({
              page: dealsDetail.pagination.currentPage + 1,
              filter: baseFilters,
              limit: 20,
            })
          );
        }
        break;
  
      default:
        break;
    }
  };
  

  const handleViewMore = (assetType) => {
    switch (assetType) {
      case "topLeadsContent":
        dispatch(
          fetchPerformanceTopContentGeneratingLeads({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
  
      case "topClosedDealsContent":
        dispatch(
          fetchPerformanceTopContentClosedDeals({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
  
      case "leadsDetail":
        dispatch(
          fetchLeadsDetailReport({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;

      case "dealsDetail":
        dispatch(
          fetchDealsDetailReport({
            page: 0,
            limit: 20,
            filter: baseFilters,
          })
        );
        break;
  
      default:
        break;
    }
  };
  

  useEffect(() => {
    if (!baseFilters) return;

    trackReportsToggledView({ tabType: _.capitalize(ReportTabs.performance) });
  
    dispatch(fetchPerformanceInteractionsGraphReport({filter: baseFilters }));
    dispatch(fetchPerformanceLeadsGraphReport({filter: baseFilters }));
    dispatch(fetchPerformanceDealsGraphReport({filter: baseFilters }));
    dispatch(fetchPerformanceDealValueGraphReport({filter: baseFilters }));
    dispatch(fetchPerformanceClosedDealsGraphReport({filter: baseFilters }));
    dispatch(fetchPerformanceClosedDealValueGraphReport({filter: baseFilters }));
    dispatch(fetchPerformanceTopContentGeneratingLeads({ page: 0, limit: 5, filter: baseFilters }));
    dispatch(fetchPerformanceTopContentClosedDeals({ page: 0, limit: 5, filter: baseFilters }));
  },[JSON.stringify(baseFilters)]);

  const onContactClick = (contactId: string) => {
    setContactInfo({
      show: true,
      contactId,
    });
  };

  return (
    <div className={styles.cardsContainer}>
      <EntityChart
        title={`# of Interactions powered by content (${interactions?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Track the number of shares associated with deals in your CRM on a daily basis"
        data={interactions?.item}
        legend={{ key: "Date", value: "Total Interactions" }}
        containerClassName={styles.chartContainer}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        loading={interactions?.loading}
        tabName={ReportTabs.performance}
        tableName={PerformanceTables.interactionContent}
      />
      <EntityInsightPanel
        title={`# of Leads (${leads?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Monitor daily lead generation activity through gated shares to assess growth and effectiveness of your content"
        data={leads?.item}
        reportDate={reportDate}
        filters={validFilters}
        legend={{ key: "Date", value: "Total Leads" }}
        loadMore={() => handleLoadMore("leadsDetail")}
        viewMore={() => handleViewMore("leadsDetail")}
        pagination={leadsDetail?.pagination}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        scrollTitle={`Leads: Details(${leadsDetail?.pagination.totalRecordsCount})`}
        items={leadsDetail?.items}
        graphLoading={leads?.loading}
        loading={leadsDetail?.loading}
        downloadConfig={{
          name: "performance_leads",
          schema: LEADS_DETAILS_SCHEMA,
          fileName: "Performance Leads",
          title: "Leads Details Reports",
          reportType: "leadsDetail",
        }}
        columns={["Date Added", "Account", "Content Used", "Source"]}
        titleColumnName="Lead Name"
        renderScrollItem={({ item, columnCount }) => (
          <LeadListItem
            item={item}
            columnCount={columnCount}
            onContactClick={onContactClick}
          />
        )}
        tabName={ReportTabs.performance}
        tableName={PerformanceTables.leads}
      />
      <EntityInsightPanel
        title={`# of Deals (${deals?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Track daily deal creation to stay informed on your team's  sales progress and productivity"
        data={deals?.item}
        reportDate={reportDate}
        filters={validFilters}
        legend={{ key: "Date", value: "Total Deals" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        scrollTitle={`Deals: Details(${dealsDetail?.pagination?.totalRecordsCount || 0})`}
        items={dealsDetail?.items}
        graphLoading={deals?.loading}
        loading={dealsDetail?.loading}
        pagination={dealsDetail?.pagination}
        loadMore={() => handleLoadMore("dealsDetail")}
        viewMore={() => handleViewMore("dealsDetail")}
        downloadConfig={{
          name: "performance_deals",
          schema: DEALS_DETAILS_SCHEMA,
          fileName: "Performance Deals",
          title: "Deals Details Reeports",
          reportType: "dealsDetail",
        }}
        columns={["Date Added", "Value", "Status", "Content Used"]}
        titleColumnName="Deal Title"
        renderScrollItem={({ item, columnCount }) => (
          <DealListItem
            item={item}
            columnCount={columnCount}
          />
        )}
        tabName={ReportTabs.performance}
        tableName={PerformanceTables.deals}
      />
      <EntityChart
        title={`Deal Value (${dealValue?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="View the cumulative value of created deals to access overall revenue potential"
        data={filteredDealValue}
        legend={{ key: "Date", value: "Total Deal Value" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        loading={dealValue?.loading}
        tabName={ReportTabs.performance}
        tableName={PerformanceTables.dealValue}
      />
      <EntityChart
        title={`# of Closed Deals (${closedDeals?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="Track daily count of closed deals to measure sales team performance and deal closure efficiency"
        data={closedDeals?.item}
        legend={{ key: "Date", value: "Total Closed Deals" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        loading={closedDeals?.loading}
        tabName={ReportTabs.performance}
        tableName={PerformanceTables.closedDeals}
      />
      <EntityChart
        title={`Closed Deal Value (${closedDealValue?.item?.reduce((sum, { value = 0 }) => sum + value, 0) || 0})`}
        description="View the total value of deals closed to assess revenue impact and sales performance"
        data={closedDealValue?.item}
        legend={{ key: "Date", value: "Total Closed Deal Value" }}
        valueFormatter={(value) => `${value.toLocaleString()}`}
        loading={closedDealValue?.loading}
          tabName={ReportTabs.performance}
        tableName={PerformanceTables.closedDealValue}
      />

      <ReportListView
        title={`Top content that generates leads ? (${topLeadsContent.pagination.totalRecordsCount})`}
        description="Identify top-performing content that drives lead generation, enabling targeted content strategies for optimal results"
        items={topLeadsContent.items}
        loading={topLeadsContent.loading}
        pagination={topLeadsContent.pagination}
        canDownload={_.get(topLeadsContent, 'pagination.totalRecordsCount', 0) <= 5}
        reportDate={reportDate}
        filters={validFilters}
        loadMore={() => handleLoadMore("topLeadsContent")}
        viewMore={() => handleViewMore("topLeadsContent")}
        downloadConfig={{
          name: "performance_content_leads",
          schema: CONTENT_LEADS_SCHEMA,
          fileName: "Performance Content Leads",
          title: "Content Leads Reports",
          reportType: "topLeadsContent",
        }}
        renderItem={({ item }) => <LeadsContentCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <LeadsContentListItem
            item={item}
            columnCount={columnCount}
          />
        )}
        columns={["Interactions", "Leads"]}
        firstColumn="Content Name"
            tabName={ReportTabs.performance}
        tableName={PerformanceTables.topGenerateLeads}
      />
      <ReportListView
        title={`Top content that closed deals ? (${contentClosedDeals.pagination.totalRecordsCount})`}
        description="Discover the most impactful content that contributes to deal closures, facilitating strategic content distribution for successful sales outcomes"
        items={contentClosedDeals.items}
        loading={contentClosedDeals.loading}
        reportDate={reportDate}
        filters={validFilters}
        pagination={contentClosedDeals.pagination}
        canDownload={_.get(contentClosedDeals, 'pagination.totalRecordsCount', 0) <= 5}
        loadMore={() => handleLoadMore("topClosedDealsContent")}
        viewMore={() => handleViewMore("topClosedDealsContent")}
        downloadConfig={{
          name: "performance_content_deals",
          schema: CLOSED_DEALS_SCHEMA,
          fileName: "Performance Content Deals",
          title: "Content Deals Reports",
          reportType: "contentClosedDeals",
        }}
        renderItem={({ item }) => <ClosedDealsCard item={item} />}
        renderListItem={({ item, columnCount }) => (
          <ClosedDealsListItem
            item={item}
            columnCount={columnCount}
          />
        )}
        columns={["Interactions", "Deals"]}
        firstColumn="Content Name"
        tabName={ReportTabs.performance}
        tableName={PerformanceTables.topClosedDeals}
      />
      <Contact
        show={contactInfo.show}
        contactId={contactInfo.contactId}
        onClose={() => setContactInfo({ show: false, contactId: "" })}
      />
    </div>
  );
};

export default PerformanceTab;
